import { NodeWrapper } from "./NodeWrapper";
import { mkHeaderStyle, HEADER_ICON_STYLE, HEADER_TEXT_STYLE, NODE_BODY_STYLE, NODE_INFO_ROW, mkInputHandleStyle } from "./nodeStyles";
import React, { useState, useCallback, useEffect } from 'react';
import { Box, TextField, Typography, IconButton, Tooltip, Button } from '@mui/material';
import DataObjectIcon from '@mui/icons-material/DataObjectOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Handle } from 'react-flow-renderer';
import { i18n } from '../../../translate/i18n';
import { useNodeStorage } from '../../../stores/useNodeStorage';

import { Tooltip as CoreTooltip, IconButton as CoreIconButton } from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    maxWidth: 380,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #334155',
    padding: '12px 14px',
    borderRadius: '8px',
    fontFamily: "'Inter', 'Roboto', sans-serif",
    lineHeight: 1.5,
  },
  arrow: { color: '#1e293b' },
}))(CoreTooltip);

const useStyles = makeStyles((theme) => ({
  pulseIcon: {
    color: '#ffffff',
    borderRadius: '50%',
    animation: '$glowPulse 1.5s infinite',
  },
  '@keyframes glowPulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.9)' },
    '70%': { boxShadow: '0 0 0 8px rgba(255, 255, 255, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)' },
  },
}));

const VariableNode = React.memo(({
  data,
  id,
  selected
}) => {
  const classes = useStyles();
  const [variableName, setVariableName] = useState(data?.variableName || '');
  const [variableValue, setVariableValue] = useState(data?.variableValue || '');
  const [variableType, setVariableType] = useState(data?.variableType || 'text');
  const [savedStatus, setSavedStatus] = useState(data?.savedStatus || '');
  const [showSavePopup, setShowSavePopup] = useState(false);
  const storageItems = useNodeStorage();
  useEffect(() => {
    data.variableName = variableName;
    data.variableValue = variableValue;
    data.variableType = variableType;
    data.savedStatus = savedStatus;
  }, [variableName, variableValue, variableType, savedStatus, data]);
  useEffect(() => {
    let timer;
    if (showSavePopup) {
      timer = setTimeout(() => {
        setShowSavePopup(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showSavePopup]);
  const handleSave = useCallback(() => {
    if (variableName) {
      window.flowVariables = window.flowVariables || {};
      window.flowVariables[variableName] = variableValue;
      setSavedStatus(i18n.t("flowBuilder.editor.nodes.variable.saved"));
      setShowSavePopup(true);
    }
  }, [variableName, variableValue, data]);
  return <NodeWrapper id={id} selected={selected} color="#06B6D4">

    <Handle type="target" position="left" id="variable-in" style={mkInputHandleStyle("#06B6D4")} isConnectable={true} />

    {selected}

    <div style={mkHeaderStyle("#06B6D4")}>
      <DataObjectIcon style={HEADER_ICON_STYLE} />
      <div style={HEADER_TEXT_STYLE}>{data.name || "Asignar Variable"}</div>
    
        <HtmlTooltip
          arrow
          title={
            <React.Fragment>
              <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#22d3ee' }}>
                💡 {i18n.t("flowBuilder.tooltips.variable.title")}
              </div>
              <div style={{ marginBottom: '0' }}>
                <div dangerouslySetInnerHTML={{ __html: i18n.t("flowBuilder.tooltips.variable.body") }} />
              </div>
            </React.Fragment>
          }
          placement="right"
        >
          <CoreIconButton size="small" style={{ padding: '4px', marginLeft: '2px' }} className={classes.pulseIcon}>
            <HelpOutlineIcon style={{ fontSize: '15px' }} />
          </CoreIconButton>
        </HtmlTooltip>
      </div>
    <div style={NODE_BODY_STYLE}>
      <div style={{ ...NODE_INFO_ROW, display: 'flex', flexDirection: 'column', gap: '12px' }}>

        <TextField label={i18n.t("flowBuilder.editor.nodes.variable.variableName")} size="small" value={variableName} onChange={e => setVariableName(e.target.value)} fullWidth sx={{
          mb: 1
        }} />

        <Box position="relative" sx={{ width: "100%" }}>
          <TextField label={i18n.t("flowBuilder.editor.nodes.variable.value")} size="small" value={variableValue} onChange={e => setVariableValue(e.target.value)} fullWidth multiline minRows={2} maxRows={4} helperText={i18n.t("flowBuilder.editor.nodes.variable.helperText")} />
          <Handle
            type="source"
            position="right"
            id="variable-out"
            style={{
              top: "38px",
              right: -35,
              position: "absolute",
              background: '#06B6D4',
              width: "10px",
              height: "20px",
              borderRadius: "4px",
              cursor: "pointer",
              border: '2px solid white'
            }}
            isConnectable={true}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Typography variant="caption" color={savedStatus === i18n.t("flowBuilder.editor.nodes.variable.saved") ? 'success.main' : 'text.secondary'}>
            {savedStatus}
          </Typography>

        </Box>


        {showSavePopup && <Box sx={{
          position: 'absolute',
          top: "20px",
          right: '10px',
          backgroundColor: '#4caf50',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          zIndex: 1000,
          animation: 'fadeIn 0.3s ease-in-out',
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
              transform: 'translateY(-10px)'
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}>
          <Typography variant="body2">
            {i18n.t("flowBuilder.editor.nodes.variable.saveSuccess")}
          </Typography>
        </Box>}



      </div></div></NodeWrapper>;
});
export default VariableNode;
export const getFlowVariable = name => {
  if (!window.flowVariables) {
    return undefined;
  }
  return window.flowVariables[name];
};
export const setFlowVariable = (name, value) => {
  if (!window.flowVariables) {
    window.flowVariables = {};
  }
  window.flowVariables[name] = value;
  const event = new CustomEvent('flowVariableUpdate', {
    detail: {
      name,
      value
    }
  });
  window.dispatchEvent(event);
  return value;
};
